import { cond, noop } from 'lodash-es'
import { DateTime } from 'luxon'
import { FC, useContext, useEffect, useMemo, useRef } from 'react'
import { InView } from 'react-intersection-observer'
import { LuCodesandbox, LuNavigation2, LuNavigation2Off } from 'react-icons/lu'

import { MarkerBadge } from '@/components/MarkerBadge'

import { useSettings } from '@/feature/organizations/api/useSettings'
import { MapMarker } from '@/feature/map'
import { MapContext } from '@/feature/map/context/map'
import {
  ACTIVE_MARKER,
  ActiveAssetContext,
  INACTIVE_ASSET,
  useMarkerColors,
  usePersistentObservatoryPageStore,
} from '@/feature/observatory'
import { useAssetTelemetry } from '@/feature/telemetry'

import { Asset, AssetContentType } from '@/lib/features/Assets/interfaces'

import { getTagLabel } from './AssetMarker.utils'

/** Keeps the pin at the legacy artwork's 54px width, whatever the caller passes. */
const VehicleMarker = ({ rotate, color }: { rotate?: number; color?: string }) => (
  <MarkerBadge
    glyph={LuNavigation2}
    shape="pin"
    size={54}
    color={color ?? 'currentColor'}
    rotate={rotate}
    filled
  />
)

/**
 * A tracking unit rather than a vehicle, so it carries a device glyph instead of an arrow
 * and the heading is deliberately ignored — the two markers were previously told apart
 * only by their silhouette.
 *
 * Used for both states: a unit that has lost contact keeps its glyph and changes colour,
 * where a vehicle swaps its arrow for the struck-through one.
 */
const PtuMarker = ({ color }: { color?: string }) => (
  <MarkerBadge glyph={LuCodesandbox} shape="pin" size={54} color={color ?? 'currentColor'} />
)

/**
 * Lost contact: the same arrow as above, struck through. Not `filled`, because the glyph
 * is two open fragments rather than a closed polygon. The heading is deliberately ignored.
 */
const LostContactMarker = ({ color }: { color?: string }) => (
  <MarkerBadge glyph={LuNavigation2Off} shape="pin" size={54} color={color ?? 'currentColor'} />
)

import styles from './AssetMarker.module.css'

export type AssetMarkerProps = {
  asset: Asset
  onClick?: () => void
}
// [AssetContentType, boolean].join("") is used to create a unique key for each marker
// Using a standard object would do this automatically, but it's not as descriptive
const markers = new Map<string, FC>(
  (
    [
      [[AssetContentType.PTU, true], PtuMarker],
      [[AssetContentType.PTU, false], PtuMarker],
      [[AssetContentType.VEHICLE, true], LostContactMarker],
      [[AssetContentType.VEHICLE, false], VehicleMarker],
    ] as const
  ).map(([key, value]) => [key.join(''), value]) as unknown as [string, FC][],
)
export const AssetMarker = ({ asset, onClick = noop }: AssetMarkerProps) => {
  const tracking = useAssetTelemetry(asset.vehicleBridgeId)

  const map = useContext(MapContext)
  const { activeAsset } = useContext(ActiveAssetContext)
  const vehicleLabels = usePersistentObservatoryPageStore((state) => state.vehicleLabels)

  const marker = useRef<google.maps.marker.AdvancedMarkerElement>(null)

  const isActiveAsset = activeAsset?.id === asset.id

  const { data: settings } = useSettings()
  const { moving, stationary, lostContactDefault, lostContactAfterOfflineTimeout } =
    useMarkerColors()

  const hasLostContact = useMemo(
    () =>
      tracking?.sourceDatetime != undefined &&
      DateTime.fromJSDate(tracking?.sourceDatetime) <
        DateTime.now().minus({ minutes: settings?.vehicleOfflineAfter ?? 24 * 60 }),
    [tracking?.sourceDatetime],
  )

  const color = useMemo(
    cond([
      [
        () => hasLostContact && settings?.vehicleOfflineAfter != null,
        () => lostContactAfterOfflineTimeout,
      ],
      [() => hasLostContact, () => lostContactDefault],
      [() => !!tracking?.isMoving, () => moving],
      [() => true, () => stationary],
    ]),
    [
      hasLostContact,
      settings?.vehicleOfflineAfter,
      tracking?.isMoving,
      lostContactAfterOfflineTimeout,
      lostContactDefault,
      moving,
      stationary,
    ],
  )

  const Icon = markers.get(`${asset.contentType}${hasLostContact}`) ?? VehicleMarker

  const shouldShowLabels = asset.contentType === AssetContentType.VEHICLE && tracking

  const shareLabel = useMemo(() => {
    if (!vehicleLabels.showShareReference) return undefined
    return asset.activeShareLabel || undefined
  }, [vehicleLabels.showShareReference, asset.activeShareLabel])

  const registrationLabel = vehicleLabels.showRegistration ? asset.plateNumber : undefined

  const tagLabel = useMemo(() => {
    if (!vehicleLabels.showTags) return undefined
    return getTagLabel(asset.tags)
  }, [vehicleLabels.showTags, asset.tags])

  const hideLabelsForOtherAssets = !!activeAsset && activeAsset.id !== asset.id

  const showAnyLabel =
    shouldShowLabels && !hideLabelsForOtherAssets && (registrationLabel || shareLabel || tagLabel)

  useEffect(() => {
    if (tracking && map && isActiveAsset && marker.current) {
      map.panTo(
        {
          position: {
            //magic number to focus the marker just a little bit more to the top
            lat: tracking.latitude - 0.004,
            lng: tracking.longitude,
          },
          zoom: 15,
        },
        1000,
      )
    }
  }, [tracking, isActiveAsset, map, map?.panTo, asset, asset.id, marker.current])

  return (
    <>
      {tracking && (
        <MapMarker
          ref={marker}
          position={{ lat: tracking.latitude, lng: tracking.longitude }}
          onClick={() => onClick()}
          className={isActiveAsset ? styles.markerPoint : undefined}
          zIndex={isActiveAsset ? ACTIVE_MARKER : INACTIVE_ASSET}
        >
          <InView>
            {({ ref }) => {
              return (
                <div ref={ref} role="button" className={styles.marker}>
                  {showAnyLabel ? (
                    <div className={styles.labels}>
                      {registrationLabel ? (
                        <span className={styles.labelChip}>{registrationLabel}</span>
                      ) : null}
                      {shareLabel ? <span className={styles.labelChip}>{shareLabel}</span> : null}
                      {tagLabel ? (
                        <span className={styles.labelChip} data-testid="asset-marker-tag-label">
                          {tagLabel}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <span className={styles.centeredAssetIcon}>
                    <Icon rotate={tracking?.heading ?? 0} color={color} />
                  </span>
                </div>
              )
            }}
          </InView>
        </MapMarker>
      )}
    </>
  )
}
